import { readItem, removeItem, writeItem } from '../../../core';

/**
 * analySessionStore — **진행 중** 회차의 서버 루트 id.
 *
 * 진행 레코드(`diagnosisProgress`)가 아니라 **전용 키**에 담는다. `saveProgress`는 첫
 * 테이크를 마쳐야 불리는데(`useRecordMachine`의 `advance`), 루트는 그보다 앞선 인트로
 * CTA에서 생긴다 — 진행 레코드에 넣으면 첫 테이크 전에 이탈한 사용자가 재진입할 때 루트를
 * 하나 더 만든다. 그게 플로우에서 이탈이 가장 잦은 지점이다. 진행 레코드를 CTA 직후에 미리
 * 쓰는 방법도 있으나 `isProgress()`가 클립 없는 레코드를 거부해, 검증기를 풀면 진행 스키마
 * 변경(`DIAGNOSIS_PROGRESS_VERSION` 상승)까지 끌고 온다.
 *
 * 값은 서버 id 문자열 하나다(`<uid>:<analyNo>`). JSON으로 싸지 않는 이유: 이 키는 모바일
 * WebView가 네이티브로 미러링하는 값이라(diagnosisStore 주석) 작을수록 좋고, 형태가 없으면
 * 버전도 필요 없다.
 */

export const ANALY_SESSION_KEY = 'muzly.analy.session';

/** 진행 중 회차의 루트 id. 없거나 빈 문자열이면 `null`이다. */
export const loadAnalySessionId = (): string | null =>
  readItem(ANALY_SESSION_KEY) || null;

/**
 * 회차 세대. `clearAnalySessionId`가 올린다.
 *
 * 마감(`useRecordMachine`의 `finish`)은 루트 생성이 아직 날고 있어도 키를 지운다. 그
 * 생성이 나중에 끝나며 id를 저장하면 **끝난 회차의 루트가 살아남아** 다음 회차가 그것을
 * 재사용하고, 서버는 `done` 루트에 들어오는 제출을 409로 잠근다(백엔드
 * `submit-take.ts` `.state[done] is locked`). 409는 `analySession`에서 회차 종료로
 * 읽혀 남은 큐까지 버리므로, 다음 회차 네 테이크가 통째로 사라진다.
 *
 * 세대를 붙들고 저장 직전에 대조하면 그 저장만 조용히 빠진다 — 마감 중인 회차 자신의
 * 제출은 생성 **반환값**으로 id를 받으므로 영향이 없다.
 */
let generation = 0;

export const analySessionGeneration = (): number => generation;

/**
 * 루트 id를 남긴다. `atGeneration`이 지금 세대와 다르면(그 사이 회차가 마감됐다) 저장하지
 * 않는다. 인자를 안 주면 지금 세대라 항상 저장한다.
 */
export const saveAnalySessionId = (
  id: string,
  atGeneration: number = generation,
): void => {
  if (atGeneration !== generation) {
    return;
  }
  writeItem(ANALY_SESSION_KEY, id);
};

/**
 * 이 회차는 더 쓰지 않는다. 부르는 자리가 둘이다 — 진단을 **마칠 때**(`finish`)와 진행
 * 중이던 회차를 **버릴 때**(홈 이어하기 팝업의 "새로 시작"). 버릴 때 안 지우면 새 회차가
 * 버린 회차의 루트를 물려받아, 서버에는 두 회차가 한 회차로 겹쳐 남는다.
 *
 * 재녹음(`restartAll`)에서는 지우지 않는다: 그쪽은 인트로를 다시 거치지 않아 루트를 만들
 * 자리가 없고, 테이크는 복합키라 같은 루트에 덮어써도 안전하다(D18·C5). 이어하기도
 * 마찬가지로 남긴다 — 같은 회차를 잇는 것이라 루트도 같아야 한다.
 */
export const clearAnalySessionId = (): void => {
  generation += 1;
  removeItem(ANALY_SESSION_KEY);
};
